import lodashGet from "lodash/get";
import lodashIsEqual from "lodash/isEqual";

import { isEmptyOrSpaces } from "@/common/basic.utilities.js";
import {
  KEYWORD_MAX_LIMIT_EXT,
  ALERT_AOI_MAX_AREA,
  WORLD_WIDE_AOI,
} from "@/common/constants/alert.js";
import { getPrimaryTopics } from "@/services/events/topics.service";
import { getFeatureArea } from "@/common/maps/utilities";

export { _validateAlert as validateAlert };
export { _validateFrequency as validateFrequency };
export { _validateShare as validateShare };
export { _validateTripwires as validateTripwires };
export { _validateEvents as validateEvents };
export { _validateVerifiedEvents as validateVerifiedEvents };
export { _validateKeywords as validateKeywords };
export { _validateAoi as validateAoi };
export { _isAlertGeometryTooLarge as isAlertGeometryTooLarge };

const _validateAlert = (alert) => {
  const name = !isEmptyOrSpaces(alert.name);
  const type = _validateTripwires(alert);
  const frequency = _validateFrequency(alert);
  const share = _validateShare(alert);
  const aoi = _validateAoi(alert);
  return {
    overall: name && share && type && frequency,
    name,
    share,
    type,
    aoi,
    frequency,
  };
};
const _validateAoi = (alert) => {
  return !!(
    (lodashGet(alert, "aoiFeature.properties") &&
      lodashIsEqual(alert.aoiFeature.properties, WORLD_WIDE_AOI.properties)) ||
    (lodashGet(alert, "aoiFeature.properties.aoiIds") &&
      Array.isArray(alert.aoiFeature.properties.aoiIds) &&
      alert.aoiFeature.properties.aoiIds.length) ||
    (lodashGet(alert, "aoiFeature.geometry.coordinates") &&
      Array.isArray(alert.aoiFeature.geometry.coordinates) &&
      alert.aoiFeature.geometry.coordinates.length > 0 &&
      !_isAlertGeometryTooLarge(alert))
  );
};
const _validateTripwires = (alert) => {
  const hotspotsEnabled = alert.hotspotsEnabled;
  let validPulse = _validatePulse(alert);
  if (validPulse) {
    if (alert.pulseDetails) {
      for (let i = 0; i < alert.pulseDetails.length; i++) {
        validPulse =
          (parseInt(alert.pulseDetails[i].lowerBound) > 0 &&
            parseInt(alert.pulseDetails[i].lowerBound) <= 100) ||
          alert.pulseDetails[i].useLowestDynamicBound;
        if (!validPulse) {
          break;
        }
      }
    }
  }
  const eventsSelection = _validateEvents(alert);
  const verifiedEventsSelection = _validateVerifiedEvents(alert);
  const validSources = alert.sources && alert.sources.length > 0;
  const validKeywords = _validateKeywords(alert);
  return (
    hotspotsEnabled ||
    validPulse ||
    eventsSelection ||
    verifiedEventsSelection ||
    (validSources && validKeywords)
  );
};

const _validateEvents = (alert) => {
  const HAS_TRAVEL = lodashGet(alert, "eventsSelection.travel");

  let hasEvent = false;
  const PRIMARY_TOPICS = getPrimaryTopics();
  PRIMARY_TOPICS.forEach((topic) => {
    const EVENT = lodashGet(alert, `eventsSelection[${topic.type}].categories`);
    if (EVENT && Array.isArray(EVENT) && EVENT.length) {
      hasEvent = true;
    }
  });

  if (HAS_TRAVEL || hasEvent) {
    return true;
  }
  return false;
};

const _validateVerifiedEvents = (alert) => {
  let isValid = false;
  if (lodashGet(alert, "verifiedEventsSelection.type")) {
    isValid =
      alert.verifiedEventsSelection.type === "maritime" ||
      (alert.verifiedEventsSelection.type === "security" &&
        lodashGet(alert, "verifiedEventsSelection.categories") &&
        !!alert.verifiedEventsSelection.categories.length);
  }
  return isValid;
};

const _validateKeywords = (alert) => {
  const keywordInfo = alert.keywords;
  const hasKeywordInfo = !!(
    keywordInfo &&
    keywordInfo.includes &&
    keywordInfo.includes.length > 0 &&
    keywordInfo.includes[0].values &&
    keywordInfo.includes[0].values.length > 0
  );
  if (!hasKeywordInfo) {
    return false;
  }

  const excludesCount = keywordInfo.excludes ? keywordInfo.excludes.length : 0;
  const totalCount = keywordInfo.includes.reduce((total, include) => {
    total += include.values ? include.values.length : 0;
    return total;
  }, excludesCount);

  return totalCount <= KEYWORD_MAX_LIMIT_EXT;
};

const _validatePulse = (alert) => {
  if (alert.pulseEnabled) {
    let aoiIds = lodashGet(alert, "aoiFeature.properties.aoiIds");
    if (aoiIds) {
      let nonCountryAoiIds = aoiIds.filter((entry) => {
        return entry.length !== 2;
      });
      return nonCountryAoiIds && nonCountryAoiIds.length === 0;
    }
  }
  return false;
};

const _validateFrequency = (alert) => {
  const alertExpiration = alert.expiration;
  const alertFrequency = alert.frequency;

  if (!(alertExpiration && alertFrequency)) {
    return false;
  }

  if (alertFrequency && alertFrequency.id === "bad") {
    return false;
  }

  if (alert.pulseEnabled && alertFrequency.id === "15") {
    console.log("failing because of 15");
    return false;
  }

  const validExpiration =
    !alertExpiration.id || alertExpiration.id >= 1 || alertExpiration.id <= 6;

  if (validExpiration && alertFrequency) {
    return true;
  } else {
    return false;
  }
};

const _validateShare = (alert) => {
  if (!_validateFrequency(alert)) {
    return false;
  }
  const alertShare = alert.share.type;
  if (!alertShare) {
    return false;
  }
  if (alertShare.id === 1) {
    return true;
  } else {
    return alert.share.list.length > 0;
  }
};

const _isAlertGeometryTooLarge = (alert) => {
  if (
    alert.aoiFeature &&
    alert.aoiFeature.geometry &&
    alert.aoiFeature.geometry.coordinates &&
    alert.aoiFeature.geometry.coordinates.length > 0
  ) {
    let area = getFeatureArea(alert.aoiFeature);
    area = Math.round(area / 1000000);
    if (area <= ALERT_AOI_MAX_AREA) {
      return false;
    }
  }
  return true;
};
